import { useMutation } from '@tanstack/react-query'
import { playerRepository } from '../repositories/playerRepository'
import { storageRepository } from '../repositories/storageRepository'
import { auditRepository } from '../repositories/auditRepository'
import { sportsRepository } from '../repositories/sportsRepository'
import { calculateFee } from '@/lib/services/feeCalculation'
import type { RegistrationFormData, RegistrationSubmitResult } from '@/lib/types/registration'
import { useRegistrationStore } from '@/lib/store/registrationStore'

interface SubmitRegistrationParams {
  formData: RegistrationFormData
  profilePhotoUrl: string
  paymentScreenshotUrl: string
}

export const useSubmitRegistration = () => {
  const store = useRegistrationStore()

  return useMutation({
    mutationFn: async ({ formData, profilePhotoUrl, paymentScreenshotUrl }: SubmitRegistrationParams): Promise<RegistrationSubmitResult> => {
      // Step 1: Check for duplicate
      const duplicate = await playerRepository.checkDuplicatePlayer({
        fullName: formData.fullName,
        mobileNumber: formData.mobileNumber,
      })

      if (duplicate) {
        // Log duplicate attempt (non-blocking)
        await auditRepository.logDuplicateAttempt({
          existingPlayerId: duplicate.player_id,
          attemptedFullName: formData.fullName,
          attemptedMobileNumber: formData.mobileNumber,
          attemptedSportIds: formData.selectedSportIds,
          attemptedSportNames: duplicate.sport_names,
        })

        // Set duplicate data in store to show dialog
        store.setDuplicateData(duplicate)
        store.setShowDuplicateDialog(true)

        throw new Error('DUPLICATE_REGISTRATION')
      }

      if (!profilePhotoUrl || !paymentScreenshotUrl) {
        throw new Error('Images must be uploaded before submission')
      }

      // Step 3: Calculate expected fee
      const expectedAmount = calculateFee(formData.selectedSportIds.length)

      // Step 4: Create player
      const playerId = await playerRepository.createPlayer({
        formData,
        photoUrl: profilePhotoUrl,
        paymentUrl: paymentScreenshotUrl,
        expectedAmount,
      })

      // Step 5: Create player sports
      await playerRepository.createPlayerSports(playerId, formData.selectedSportIds)

      // Step 6: Check if pickleball is enrolled
      const sports = await sportsRepository.fetchActiveSports()
      const isPickleballEnrolled = formData.selectedSportIds.some((id) => {
        const sport = sports.find((s) => s.id === id)
        return sport?.name.toLowerCase().includes('pickleball') ||
               sport?.display_text.toLowerCase().includes('pickleball')
      })

      return { playerId, isPickleballEnrolled }
    },
  })
}
